import type { CompanyConfigs, ClientValidationResponse, STClientAPIAccess } from "@stss/types";
import axios from "axios";
import type { AxiosRequestConfig, AxiosError } from "axios";
import CryptoJS from "crypto-js";
import type WordArray from "crypto-js/lib-typedarrays";

const ST_VALIDATION_API_URL = process.env.NEXT_PUBLIC_ST_VALIDATION_API_URL || "";
const ST_VALIDATION_API_KEY = process.env.NEXT_PUBLIC_ST_VALIDATION_API_KEY || "";
const validationAPIHeaders = {
  "Content-Type": "application/json",
  "x-api-key": ST_VALIDATION_API_KEY,
};

export function createAccess(configs: CompanyConfigs): STClientAPIAccess {
  const { clientApiUrl, clientAppSettings } = configs;
  const settings = clientAppSettings?.WEBAPP;

  if (!settings) {
    throw new Error("Client app settings for WEBAPP not found");
  }

  const sha1 = (data: string | WordArray) => {
    return CryptoJS.SHA1(data).toString(CryptoJS.enc.Hex);
  };

  const hash = (clientAppId: string, clientAppKey: string, salt: string) => {
    return sha1(clientAppId + clientAppKey.replace(/\\/g, "") + salt);
  };

  return {
    url: clientApiUrl,
    headers: {
      "Content-Type": "application/json",
      "Client-App-Id": settings.clientAppId,
      "Client-App-Hash": hash(settings.clientAppId, settings.clientAppKey, settings.salt),
    },
  };
}

export async function getCompanyConfigsByID(companyID: string) {
  const config: AxiosRequestConfig = {
    method: "GET",
    url: `${ST_VALIDATION_API_URL}/v1/clients/st/configs/${companyID}`,
    headers: validationAPIHeaders,
  };
  return new Promise<CompanyConfigs>((resolve, reject) => {
    axios(config)
      .then((response) => {
        const configData = response.data?.data || response.data;
        resolve(configData);
      })
      .catch((e: AxiosError) => {
        reject(e);
      });
  });
}

export async function loginSTUser(email: string, clientId?: string) {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: `${ST_VALIDATION_API_URL}/v1/validate/st`,
    headers: validationAPIHeaders,
    data: {
      email: email,
      ...(clientId && { client_id: clientId }),
    },
  };
  return new Promise<ClientValidationResponse>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e: AxiosError) => {
        reject(e);
      });
  });
}

export async function validateUserByEmail(email: string, clientId?: string) {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: `${ST_VALIDATION_API_URL}/v1/validate/all`,
    headers: validationAPIHeaders,
    data: {
      email: email,
      ...(clientId && { client_id: clientId }),
    },
  };
  return new Promise<ClientValidationResponse>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e: AxiosError) => {
        reject(e);
      });
  });
}

export const getClientAccessByUserEmail = async (email: string): Promise<STClientAPIAccess> => {
  try {
    const loginResponse = await loginSTUser(email);
    if (!loginResponse.email_exists || !loginResponse.data?.client_id) {
      return Promise.reject(new Error("User not found or client ID missing"));
    }
    const configs = await getCompanyConfigsByID(loginResponse.data.client_id);
    return Promise.resolve(createAccess(configs));
  } catch (e) {
    return Promise.reject(e);
  }
};
